import { } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { Delete as DeleteIcon, DragIndicator, Image as ImageIcon } from '@mui/icons-material';
import CkEditor from './CkEditor';

export type ContentBlock =
  | { id: string; type: 'richtext'; html: string }
  | { id: string; type: 'paragraph'; text: string } // legacy support for existing data
  | { id: string; type: 'list'; items: string[] } // legacy support
  | { id: string; type: 'image'; url: string; alt?: string };

export interface BlockEditorProps {
  value: ContentBlock[];
  onChange: (next: ContentBlock[]) => void;
  onPickImage?: () => Promise<{ url: string; alt?: string } | null>;
}

export default function BlockEditor({ value, onChange, onPickImage }: BlockEditorProps) {
  const blocks = value || [];

  const move = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const next = [...blocks];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onChange(next);
  };

  const addRichText = () => onChange([...(blocks || []), { id: crypto.randomUUID(), type: 'richtext', html: '' }]);
  const addImage = async () => {
    if (!onPickImage) return;
    const picked = await onPickImage();
    if (!picked) return;
    onChange([...(blocks || []), { id: crypto.randomUUID(), type: 'image', url: picked.url, alt: picked.alt }]);
  };

  const setBlock = (index: number, next: ContentBlock) => {
    const arr = [...blocks];
    arr[index] = next;
    onChange(arr);
  };

  const remove = (index: number) => {
    const arr = [...blocks];
    arr.splice(index, 1);
    onChange(arr);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Button size="small" variant="outlined" onClick={addRichText}>テキスト</Button>
        <Button size="small" variant="outlined" startIcon={<ImageIcon />} onClick={addImage}>画像</Button>
      </Box>

      <Box>
        {blocks.map((block, index) => (
          <Box
            key={block.id}
            sx={{ p: 1, mb: 1, border: '1px solid #eee', borderRadius: 1, bgcolor: '#fafafa' }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const fromIndex = Number(e.dataTransfer.getData('text/plain'));
              if (!Number.isNaN(fromIndex)) move(fromIndex, index);
            }}
          >
            {/* ドラッグハンドル部分 - 確実にドラッグ可能 */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 1,
                p: 0.5,
                bgcolor: '#f5f5f5',
                borderRadius: 0.5,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' }
              }}
              draggable={true}
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', String(index));
                e.dataTransfer.effectAllowed = 'move';
              }}
            >
              <IconButton size="small" sx={{ cursor: 'grab', pointerEvents: 'none' }}>
                <DragIndicator fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ flex: 1, pointerEvents: 'none' }}>{block.type}</Typography>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  remove(index);
                }}
                sx={{ pointerEvents: 'auto' }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            {block.type === 'richtext' && (
              <CkEditor html={block.html} onChange={(html) => setBlock(index, { ...block, html })} />
            )}
            {/* legacy editors (read/write kept for後方互換) */}
            {block.type === 'paragraph' && (
              <TextField fullWidth multiline minRows={2} value={block.text} onChange={(e) => setBlock(index, { ...block, text: e.target.value })} />
            )}
            {block.type === 'list' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {block.items.map((item, i) => (
                  <TextField key={i} fullWidth value={item} onChange={(e) => {
                    const items = [...block.items];
                    items[i] = e.target.value;
                    setBlock(index, { ...block, items });
                  }} />
                ))}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={() => setBlock(index, { ...block, items: [...block.items, ''] })}>行追加</Button>
                  <Button size="small" disabled={block.items.length <= 1} onClick={() => setBlock(index, { ...block, items: block.items.slice(0, -1) })}>末尾削除</Button>
                </Box>
              </Box>
            )}
            {block.type === 'image' && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  label="画像URL"
                  value={block.url}
                  onChange={(e) => setBlock(index, { ...block, url: e.target.value })}
                />
                <TextField
                  label="代替テキスト"
                  value={block.alt || ''}
                  onChange={(e) => setBlock(index, { ...block, alt: e.target.value })}
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// CKEditorに置き換え済み


